import React, { FunctionComponent } from "react";
import {
  MedigapPlan,
  MedigapPlanType,
  MedigapPlanPremiumRange,
} from "../utils/plans";
import { $$Whole, useTranslate } from "../../../helpers";
import PlanDetailsTable from "./PlanDetailsTable";
import { Tooltip } from "../../../components";

interface WhatYouWillPayProps {
  planData: MedigapPlan;
  planType: MedigapPlanType;
  planPremiumRanges: MedigapPlanPremiumRange;
}

const WhatYouWillPay: FunctionComponent<WhatYouWillPayProps> = ({
  planData,
  planType,
  planPremiumRanges,
}) => {
  const t = useTranslate();

  const hasCopayTooltip = [MedigapPlanType.N, MedigapPlanType.MN_N].includes(
    planType
  );

  const title = t("medigap.plan_details.what_you_will_pay");
  const data = [
    {
      property: t("medigap.plan_details.monthly_cost"),
      value: (
        <span>
          {`${$$Whole(
            Math.round(planPremiumRanges?.monthly_rate_min)
          )} - ${$$Whole(Math.round(planPremiumRanges?.monthly_rate_max))}`}
          <span aria-labelledby="monthly-cost">*</span>
        </span>
      ),
      tooltip: t("medigap.plan_details.monthly_cost_tooltip"),
    },
    planData.total_deductible
      ? {
          property: t("medigap.plan_details.plan_deductible"),
          value: planData.total_deductible,
          tooltip: t("medigap.plan_details.plan_deductible_tooltip"),
          hide: ![
            MedigapPlanType.HIGH_F,
            MedigapPlanType.HIGH_G,
            MedigapPlanType.MN_HIGH_F,
            MedigapPlanType.MN_HIGH_F_NEW,
            MedigapPlanType.WI_HIGH_DEDUCTIBLE,
          ].includes(planType),
        }
      : null,
    {
      property: t("medigap.plan_details.hospital_part_a_deductible"),
      value: planData.part_a_deductible_note
        ? `${t(planData.part_a_deductible)} ${t(
            planData.part_a_deductible_note
          )}`
        : t(planData.part_a_deductible),
      tooltip: t("medigap.plan_details.hospital_part_a_deductible_tooltip"),
    },
    {
      property: t("medigap.plan_details.medical_part_b_deductible"),
      value: planData.part_b_deductible_note
        ? `${t(planData.part_b_deductible)} ${t(
            planData.part_b_deductible_note
          )}`
        : t(planData.part_b_deductible),
      tooltip: t("medigap.plan_details.medical_part_b_deductible_tooltip"),
    },
    {
      property: t("medigap.plan_details.part_b_copays"),
      value: (
        <>
          {t(planData.part_b_coinsurance)}{" "}
          {planData.part_b_coinsurance_note &&
            t(planData.part_b_coinsurance_note)}{" "}
          {hasCopayTooltip && (
            <Tooltip
              title={t("medigap.20_50_copays")}
              triggerText={t("medigap.20_50_copays")}
              inline
            >
              <p className="ds-u-font-size--small">
                {t("medigap.20_50_copays.tooltip")}
              </p>
            </Tooltip>
          )}
        </>
      ),
      tooltip: t("medigap.plan_details.part_b_copays_tooltip"),
    },
    planData.out_pocket_limit
      ? {
          property: t("medigap.plan_details.part_b_maximum"),
          value: t(planData.out_pocket_limit),
          tooltip: t("medigap.plan_details.part_b_maximum_tooltip"),
          hide: ![
            MedigapPlanType.K,
            MedigapPlanType.L,
            MedigapPlanType.MN_SUPP_50,
            MedigapPlanType.MN_SUPP_75,
            MedigapPlanType.WI_25_COST,
            MedigapPlanType.WI_50_COST,
          ].includes(planType),
        }
      : null,
  ];

  const footerNotes = [
    {
      text: t("medigap.plan_details.excludes_part_b_premium_footnote"),
      id: "monthly-cost",
    },
  ];

  return <PlanDetailsTable table={{ title, data, footerNotes }} />;
};

export default WhatYouWillPay;
